/**
 * @description CoreCheckoutStepMixin implementation
 *
 * @param CheckoutStepMixin Base widget for extending
 */
function CoreCheckoutStepMixinClassCreator(CheckoutStepMixin: import('widgets/checkout/CheckoutStepMixin').TCheckoutStepMixin) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class CoreCheckoutStepMixin
     * @augments CheckoutStepMixin
     * @classdesc Represents generic checkout step UI logic. This components handle
     * - Activate/Deactivate step sections (Header, Body, Summary)
     * Main step activation / deactivation logic handled inside CheckoutMgr
     */
    class CoreCheckoutStepMixin extends CheckoutStepMixin {
        prefs() {
            return {
                isMultiPageCheckout: false,
                ...super.prefs()
            };
        }

        /**
         * @description Handle activate step logic
         */
        activateStep(): void {
            super.activateStep();
            const currentStepName = this.currentStep;

            this.ref(`${currentStepName}Step`).addClass('m-active');
            this.ref(`${currentStepName}Step`).get()?.setAttribute('data-tau-unique', `checkout_${currentStepName}`);

            if (this.prefs().isMultiPageCheckout) {
                this.ref(`${currentStepName}Step`).show();

                if (currentStepName === 'summary') {
                    this.ref(`${currentStepName}Header`).show();
                    this.prefs()
                        .stepsList.split(',')
                        .filter(step => step !== 'summary')
                        .forEach(step => {
                            this.ref(`${step}Step`).show();
                        });
                }
            }

            document.querySelectorAll<HTMLElement>(`[data-ref="${currentStepName}Section"]`).forEach(el => {
                el.removeAttribute('hidden');
                el.style.removeProperty('display');
            });

            this.ref(`${currentStepName}Summary`).get()?.style.setProperty('display', 'none');
        }

        /**
         * @description Handle deactivate step logic
         * @param stepName Step name that will be deactivated
         * @param isStepFinished Flag to detect if step is already finished
         */
        deactivateStep(stepName: string, isStepFinished: boolean): void {
            super.deactivateStep(stepName, isStepFinished);

            this.ref(`${stepName}Step`).removeClass('m-active').attr('data-tau-unique', false);

            if (this.prefs().isMultiPageCheckout) {
                this.ref(`${stepName}Step`).hide();
                this.ref(`${stepName}Header`).hide();
            }

            document.querySelectorAll<HTMLElement>(`[data-ref="${stepName}Section"]`).forEach(el => {
                el.hidden = true;
                el.setAttribute('hidden', '');
                el.style.setProperty('display', 'none');
            });

            const summaryElements = document.querySelectorAll<HTMLElement>(`[data-ref="${stepName}Summary"]`);

            if (isStepFinished) {
                summaryElements.forEach(this.showStepSummary);
            } else {
                summaryElements.forEach(this.hideStepSummary);
            }
        }

        /**
         * @description Set focus on the step activation
         * @param currentStep Current checkout step
         */
        setFocusOnFirstInput(currentStep: string) {
            if (currentStep === 'shipping') {
                return;
            }

            super.setFocusOnFirstInput(currentStep);
        }

        private showStepSummary(el: HTMLElement): void {
            el.hidden = false;
            el.removeAttribute('hidden');
            el.style.removeProperty('display');
        }

        private hideStepSummary(el: HTMLElement): void {
            el.hidden = true;
            el.setAttribute('hidden', '');
            el.style.setProperty('display', 'none');
        }
    }

    return CoreCheckoutStepMixin;
}

export type TCoreCheckoutStepMixin = ReturnType<typeof CoreCheckoutStepMixinClassCreator>;

export type TCoreCheckoutStepMixinInstance = InstanceType<TCoreCheckoutStepMixin>;

export default CoreCheckoutStepMixinClassCreator;
